import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { Box, Divider} from '@mui/material';
import InfoItemBox from './InfoItemBox';
import { InfoItemBoxProps } from '../dataObjects/contentTypes';


const content: InfoItemBoxProps[] = [
  {
    title: "Appoint a Treasurer",
    blocks: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            content: "The office of treasurer is vital to an organized political committee. Under the campaign finance law, no money or anything of value can be received and no expenditures or disbursements can be made by a political committee (or any person acting under the authority of or on behalf of a political committee) while it has no treasurer.",
          }
        ]
      },
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            content: "An appointed treasurer must be in place prior to the time a political committee organizes with OCPF since the treasurer's name and address must appear on the completed Statement of Organization form.",
          }
        ]
      },
      {
        type: "subheading",
        content: "Treasurers:"
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "text",
                content: "Remain subject to all the duties and liabilities imposed by the campaign finance law until his or her written resignation of the office is received or his successor's written acceptance is filed with OCPF (or the city or town clerk);",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Authorize expenditures made on behalf of the political committee;",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Keep detailed accounts of the campaign finance activities of the political committee;",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Keep and preserve detailed records of the campaign finance activities of the political committee;",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Prepare and fill required campaign finance activity reports.",
              }
            ]
          }
        ]
      }
    ]
  },
  {
    title: "Change Treasurer",
    blocks: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            content: "If a political committee needs to appoint a new treasurer, the former treasurer should submit a written letter of resignation to the political committee. The political committee should appoint a new treasurer who will complete and sign a ",
          },
          {
            type: "link",
            text: "CPF T 101: Change of Treasurer",
            url: "https://ocpf2.blob.core.windows.net/pdf/forms/cpf_t101.pdf", 
          },
          {
            type: "text",
            content: " form. The political committee must submit the change of treasurer form and a copy of the former treasurer's resignation letter to OCPF (or the town or city clerk) within ten days following the change.",
          }
        ]
      }
    ]
  },
  {
    title: "Who Cannot Serve as the Treasurer of a Political Committee",
    blocks: [
      {
        type: "paragraph",
        content: [
          {
            type: "text",
            content: "Care must be taken when selecting a new treasurer for the political committee. There are restrictions on who may serve as the treasurer for a political committee organized in Massachusetts. Those who ",
          },
          {
            type: "text",
            isBold: true,
            content: "cannot"
          },
          {
            type: "text",
            content: " serve as the treasurer of a political committee include:",
          }
        ]
      },
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "text",
                content: "A ",
              },
              {
                type: "link",
                text: "public employee",
                url: "https://ocpf2.blob.core.windows.net/pdf/guides/fs_pub_emp.pdf", 
              },
              {
                type: "text",
                content: " (one who is employed by the Commonwealth, a county or a city or town.);",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "The Secretary of the Commonwealth;",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "A city or town clerk (except those that do not administer elections);",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "A member of a board of registrars of voters in any city or town;",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "A member of an election commission in any city or town;",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "A candidate may not be the treasurer of the political committee which has been organized on his behalf.",
              }
            ]
          }
        ]
      }
    ]
  },
  {
    title: "Additional Help & Information",
    blocks: [
      {
        type: "list",
        listType: "bullet",
        items: [
          {
            content: [
              {
                type: "text",
                content: "Campaign Finance Guide: ",
              },
              {
                type: "link",
                text: "Committee Treasurer",
                url: "https://ocpf2.blob.core.windows.net/pdf/guides/treasurerguide.pdf", 
              },
              {
                type: "text",
                content: " - Information about the role and duties of a political committee treasurer in Massachusetts.",
              }
            ]
          },
          {
            content: [
              {
                type: "text",
                content: "Public Employees and Campaigns: ",
              },
              {
                type: "link",
                text: "A Fact Sheet",
                url: "https://ocpf2.blob.core.windows.net/pdf/guides/fs_pub_emp.pdf", 
              },
              {
                type: "text",
                content: ".",
              }
            ]
          }
        ]
      }
    ]
  }
];


const TreasurerInfo = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });


  return (
      <div>
        <Box sx={{paddingBottom: 2}}>
        <Typography
              component="h1"
              variant="h4"
              align={'left'}
              data-aos={'fade-up'}
              gutterBottom
              sx={{
                fontWeight: 700,
              }}
              paddingTop={4}>
              Local Filers: How to Prepare and File Reports 
            </Typography>

            <Divider />
            </Box>
         {content.map((content, index) => (
          <InfoItemBox key={index} {...content} />
        ))}
      </div>
       
   
        
  );
};

export default TreasurerInfo;
